import React, { useCallback } from 'react';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { Box } from '@gluestack-ui/themed';
import { CourseScreenView } from './CourseScreenView';
import CourseScreenOld from './CourseScreenOld';
import { useCourseScreen } from './hooks/useCourseScreen';
import { useUserFeatures } from '../../common/hooks/useUserFeatures';
import { isUUID } from '../../common/types/ids';
import log from '../../common/utils/logger';

/**
 * CourseScreen is the container component for the student course dashboard.
 * It uses the useCourseScreen hook to manage logic and the CourseScreenView
 * for presentation.
 *
 * It respects the 'student-new-ui' feature flag to support A/B testing or
 * gradual rollout of the new architecture.
 */
const CourseScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp<Record<string, unknown>>>();
  const { isFeatureActive } = useUserFeatures();
  const isNewUiActive = isFeatureActive('student-new-ui');

  const logic = useCourseScreen();

  const {
    sortedModules,
    selectedModule,
    currentModule,
    logSelectModule,
    setSelectedModule,
    showToastMessage,
    showDialog,
    t,
  } = logic;

  const performOpenModule = useCallback(({ moduleId, courseId }: { moduleId: string; courseId: string }): void => {
    // Validate IDs at boundary
    if (!isUUID(moduleId) || !isUUID(courseId)) {
      log.warn('[CourseScreen] Invalid IDs in performOpenModule', { moduleId, courseId });
      return;
    }

    const module = sortedModules.find((m) => m.id === moduleId);
    logSelectModule({ moduleId, courseId });
    showToastMessage(
      'success',
      t('student.course.open-module-dialog.message-success', {
        newModuleTitle: module?.title,
      }),
    );
    setSelectedModule({ moduleId, courseId });
    navigation.navigate('ModuleScreen', { moduleId, courseId });
  }, [logSelectModule, navigation, setSelectedModule, showToastMessage, sortedModules, t]);

  const handleOpenModule = useCallback(({ moduleId, courseId }: { moduleId: string; courseId?: string }): void => {
    if (!moduleId) return;

    const module = sortedModules.find((m) => m.id === moduleId && (!courseId || m.courses?.[0]?.id === courseId));
    if (!module) return;

    const finalCourseId = courseId || module.courses?.[0]?.id || '';

    if (module.uiState === 'graded') {
      navigation.navigate('GradeScreen', { moduleId, courseId: finalCourseId });
      return;
    }

    if (module.uiState === 'submitted') {
      // If submitted, stay on course screen
      return;
    }

    if (selectedModule?.moduleId === moduleId && selectedModule?.courseId === finalCourseId) {
      navigation.navigate('ModuleScreen', { moduleId, courseId: finalCourseId });
      return;
    }

    if (!selectedModule?.moduleId || !selectedModule?.courseId) {
      performOpenModule({ moduleId, courseId: finalCourseId });
      return;
    }

    if (module.uiState === 'locked') {
      showDialog({
        size: 'lg',
        title: t('student.course.locked-module-dialog.title'),
        message: t('student.course.locked-module-dialog.message', {
          moduleTitle: module.title,
        }),
        buttons: [
          {
            text: t('student.course.locked-module-dialog.button-open'),
            action: 'secondary',
            onPress: () => performOpenModule({ moduleId, courseId: finalCourseId }),
          },
          {
            text: t('student.course.locked-module-dialog.button-cancel'),
            action: 'primary',
          },
        ],
      });
      return;
    }

    showDialog({
      size: 'lg',
      title: t('student.course.open-module-dialog.title'),
      message: t('student.course.open-module-dialog.message', {
        oldModuleTitle: currentModule?.title,
        newModuleTitle: module.title,
      }),
      buttons: [
        {
          text: t('student.course.open-module-dialog.button-open'),
          action: 'secondary',
          onPress: () => performOpenModule({ moduleId, courseId: finalCourseId }),
        },
        {
          text: t('student.course.open-module-dialog.button-cancel'),
          action: 'primary',
        },
      ],
    });
  }, [
    currentModule?.title,
    navigation,
    performOpenModule,
    selectedModule?.courseId,
    selectedModule?.moduleId,
    showDialog,
    sortedModules,
    t,
  ]);

  if (!isNewUiActive) {
    return <CourseScreenOld />;
  }

  return (
    <Box flex={1}>
      <CourseScreenView {...logic} handleOpenModule={handleOpenModule} />
    </Box>
  );
};

export default CourseScreen;
